import React, { useState, useEffect } from 'react';
import { PageShell } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import {
  mergeEmrSettings, defaultEmrSettings, VITAL_FIELDS, CLINICAL_CALCULATORS,
  REGIONAL_LANGUAGES, MED_INSTRUCTION_SAMPLES,
} from '../../config/emrSettings';
import { DRUG_SUGGESTIONS } from '../../config/emrCatalog';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { useHospitalDirectory } from '../../hooks/useHospitalDirectory';
import { useAuth } from '../../auth/AuthContext';

const HOSPITAL_SETTINGS_TABS = [
  { id: 'pad', label: 'Rx pad sections' },
  { id: 'meds', label: 'Medications' },
  { id: 'templates', label: 'Templates' },
  { id: 'vitals', label: 'Vitals & calculators' },
  { id: 'print', label: 'Print & languages' },
  { id: 'services', label: 'Services catalog' },
  { id: 'packages', label: 'Treatment packages' },
  { id: 'availability', label: 'Doctor availability' },
  { id: 'uhid', label: 'UHID config' },
  { id: 'sms', label: 'Message settings' },
  { id: 'queue', label: 'Q Page sections' },
  { id: 'dental', label: 'Dental chart' },
  { id: 'modules', label: 'Clinical modules' },
  { id: 'doctemplates', label: 'Doc templates' },
  { id: 'workspaces', label: 'Workspaces' },
];

const PERSONAL_SETTINGS_TABS = [
  { id: 'genie', label: 'Quick medicines' },
  { id: 'dental', label: 'Dental chart' },
  { id: 'rxtemplate', label: 'My Rx template' },
  { id: 'signature', label: 'My signature' },
];

export default function RxPadSettingsPage() {
  const { user, patchUser } = useAuth();
  const { data, loading, reload } = useLiveData(() => ipApi.getEmrSettings(), []);
  const { hospital, reload: reloadHospital } = useHospitalProfile();
  const { data: dir } = useHospitalDirectory();
  const doctors = dir?.doctors || [];
  const [tab, setTab] = useState('genie');
  const [settings, setSettings] = useState(defaultEmrSettings());
  const [genieMeds, setGenieMeds] = useState([]);
  const [genieInput, setGenieInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [svcBulk, setSvcBulk] = useState('');

  const [uhidForm, setUhidForm] = useState({ prefix: 'HCEN', nextSeq: 1000 });
  const [smsCredits, setSmsCredits] = useState(500);
  const [signaturePreview, setSignaturePreview] = useState('');
  const [regNo, setRegNo] = useState('');
  const [dentalOpPersonal, setDentalOpPersonal] = useState(false);
  const [rxPrintTemplate, setRxPrintTemplate] = useState(defaultEmrSettings().rxPrintTemplate);
  const [pkgDraft, setPkgDraft] = useState({ name: '', dept: 'General', amount: '', includes: '', duration: '' });

  const isManager = user?.role === 'manager';
  const visibleTabs = isManager
    ? [...HOSPITAL_SETTINGS_TABS, ...PERSONAL_SETTINGS_TABS.filter((t) => t.id !== 'dental')]
    : PERSONAL_SETTINGS_TABS;

  useEffect(() => {
    if (hospital) {
      setUhidForm({ prefix: hospital.uhidPrefix || 'HCEN', nextSeq: hospital.uhidNextSeq || 1000 });
      setSmsCredits(hospital.smsCredits ?? 500);
    }
  }, [hospital]);

  useEffect(() => {
    if (data) {
      setSettings(mergeEmrSettings(data.settings));
      setGenieMeds(data.genieMeds || []);
    }
    setDentalOpPersonal(!!user?.enableDentalOp);
    setRegNo(user?.regNo || '');
    setRxPrintTemplate({ ...defaultEmrSettings().rxPrintTemplate, ...(user?.rxPrintTemplate || data?.settings?.rxPrintTemplate || {}) });
  }, [data, user?.enableDentalOp, user?.rxPrintTemplate, user?.regNo]);

  useEffect(() => {
    if (!visibleTabs.some((t) => t.id === tab)) setTab(visibleTabs[0]?.id || 'genie');
  }, [visibleTabs, tab]);

  const save = async (patch = {}) => {
    setBusy(true);
    setMsg('');
    try {
      if (isManager) {
        await ipApi.patchEmrSettings({ settings, genieMeds, ...patch });
        if (tab === 'uhid' || tab === 'sms') {
          await ipApi.patchHospitalProfile({
            uhidPrefix: uhidForm.prefix,
            uhidNextSeq: Number(uhidForm.nextSeq),
            smsCredits: Number(smsCredits),
          });
          await reloadHospital?.();
        }
      } else {
        await ipApi.patchEmrSettings({ genieMeds });
        if (tab === 'dental') {
          await ipApi.patchStaffProfile({ enableDentalOp: dentalOpPersonal });
          patchUser({ enableDentalOp: dentalOpPersonal });
        }
        if (tab === 'rxtemplate') {
          await ipApi.patchStaffProfile({ rxPrintTemplate });
          patchUser({ rxPrintTemplate });
        }
        if (tab === 'signature' && regNo) {
          await ipApi.patchStaffProfile({ regNo });
          patchUser({ regNo });
        }
      }
      await reload();
      setMsg('Settings saved.');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleSection = (id) => {
    setSettings((s) => ({
      ...s,
      rxSections: s.rxSections.map((x) => (x.id === id ? { ...x, visible: !x.visible } : x)),
    }));
  };

  const moveSection = (id, dir) => {
    setSettings((s) => {
      const arr = [...s.rxSections].sort((a, b) => a.order - b.order);
      const i = arr.findIndex((x) => x.id === id);
      const j = i + dir;
      if (j < 0 || j >= arr.length) return s;
      const tmp = arr[i].order;
      arr[i].order = arr[j].order;
      arr[j].order = tmp;
      return { ...s, rxSections: arr };
    });
  };

  const addCustomSection = () => {
    const label = prompt('Custom section name');
    if (!label?.trim()) return;
    const id = `custom_${Date.now()}`;
    setSettings((s) => ({
      ...s,
      customSections: [...(s.customSections || []), { id, label: label.trim(), visible: true }],
      rxSections: [...s.rxSections, { id, label: label.trim(), visible: true, order: s.rxSections.length }],
    }));
  };

  const addSectionTemplate = (section, text) => {
    if (!text.trim()) return;
    setSettings((s) => ({
      ...s,
      sectionTemplates: {
        ...s.sectionTemplates,
        [section]: [...(s.sectionTemplates[section] || []), text.trim()],
      },
    }));
  };

  const importServicesBulk = () => {
    const lines = svcBulk.split('\n').map((l) => l.trim()).filter(Boolean);
    const added = lines.map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      return { name: parts[0], fee: Number(parts[1] || 0), category: parts[2] || 'Other' };
    });
    setSettings((s) => ({ ...s, services: [...(s.services || []), ...added] }));
    setSvcBulk('');
    setMsg(`Added ${added.length} services — click Save.`);
  };

  const addGenie = (name) => {
    const n = (name || genieInput).trim();
    if (!n || genieMeds.includes(n)) return;
    setGenieMeds((g) => [...g, n]);
    setGenieInput('');
  };

  if (loading && !data) {
    return <PageShell title="Rx pad settings" subtitle="Loading…" />;
  }

  return (
    <PageShell
      title="Rx pad & EMR settings"
      subtitle="Configure prescription pad, templates, vitals, print, and clinical modules for your hospital."
      actions={
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => save()}>
          {busy ? 'Saving…' : 'Save all settings'}
        </button>
      }
    >
      {msg && <div className={`mb ${msg.includes('fail') || msg.includes('Could') ? 'error' : 'hint'}`}>{msg}</div>}
      {!isManager && (
        <div className="info-banner mb">
          Personal Rx settings only. Hospital-wide pad, dental, modules, and UHID require a manager login.
        </div>
      )}

      <div className="period-tabs card mb">
        {visibleTabs.map((t) => (
          <button key={t.id} type="button" className={`emr-chip ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pad' && (
        <div className="card">
          <h3>Prescription pad sections</h3>
          <p className="hint">Show, hide, and reorder sections on the printed and on-screen prescription pad.</p>
          <ul className="rx-settings-list">
            {settings.rxSections.sort((a, b) => a.order - b.order).map((s) => (
              <li key={s.id} className="rx-settings-row">
                <label>
                  <input type="checkbox" checked={s.visible} onChange={() => toggleSection(s.id)} />
                  {s.label}
                </label>
                <span className="row-actions">
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => moveSection(s.id, -1)}>↑</button>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => moveSection(s.id, 1)}>↓</button>
                </span>
              </li>
            ))}
          </ul>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addCustomSection}>+ Custom section</button>
        </div>
      )}

      {tab === 'templates' && (
        <div className="card">
          <h3>Section templates</h3>
          {['symptoms', 'diagnosis', 'advice'].map((sec) => (
            <div key={sec} className="mb">
              <strong style={{ textTransform: 'capitalize' }}>{sec}</strong>
              <div className="q-tag-row" style={{ marginTop: 6 }}>
                {(settings.sectionTemplates[sec] || []).map((t) => (
                  <span key={t} className="q-tag on">{t}</span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <input className="input" placeholder={`Add ${sec} template…`} id={`tpl-${sec}`} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
                  const el = document.getElementById(`tpl-${sec}`);
                  addSectionTemplate(sec, el?.value || '');
                  if (el) el.value = '';
                }}>Add</button>
              </div>
            </div>
          ))}
          <h4 style={{ marginTop: 16 }}>Full prescription templates</h4>
          <p className="hint">Save complete Rx templates from EMR after documenting a typical case — coming from live consult copy.</p>
        </div>
      )}

      {tab === 'vitals' && (
        <div className="card">
          <h3>Vitals & calculators</h3>
          <div className="form-grid">
            {VITAL_FIELDS.map((f) => (
              <label key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={(settings.vitalsConfig?.fields || []).includes(f.id)}
                  onChange={() => {
                    setSettings((s) => {
                      const fields = s.vitalsConfig?.fields || [];
                      const next = fields.includes(f.id) ? fields.filter((x) => x !== f.id) : [...fields, f.id];
                      return { ...s, vitalsConfig: { ...s.vitalsConfig, fields: next } };
                    });
                  }}
                />
                {f.label} {f.unit && `(${f.unit})`}
              </label>
            ))}
          </div>
          <h4 style={{ marginTop: 16 }}>Clinical calculators</h4>
          <div className="q-tag-row">
            {CLINICAL_CALCULATORS.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`q-tag${(settings.vitalsConfig?.calculators || []).includes(c.id) ? ' on' : ''}`}
                onClick={() => {
                  setSettings((s) => {
                    const calcs = s.vitalsConfig?.calculators || [];
                    const next = calcs.includes(c.id) ? calcs.filter((x) => x !== c.id) : [...calcs, c.id];
                    return { ...s, vitalsConfig: { ...s.vitalsConfig, calculators: next } };
                  });
                }}
              >{c.label}</button>
            ))}
          </div>
        </div>
      )}

      {tab === 'print' && (
        <div className="card">
          <h3>Print & regional languages</h3>
          <div className="form-grid">
            <div className="field">
              <label>Font size</label>
              <input className="input" type="number" min={10} max={18} value={settings.printSettings?.fontSize || 13}
                onChange={(e) => setSettings((s) => ({ ...s, printSettings: { ...s.printSettings, fontSize: Number(e.target.value) } }))} />
            </div>
            <div className="field">
              <label>Advice language</label>
              <select className="input" value={settings.printSettings?.adviceLang || 'hi'}
                onChange={(e) => setSettings((s) => ({ ...s, printSettings: { ...s.printSettings, adviceLang: e.target.value } }))}>
                {REGIONAL_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Medication instruction language</label>
              <select className="input" value={settings.printSettings?.medInstructionLang || 'hi'}
                onChange={(e) => setSettings((s) => ({ ...s, printSettings: { ...s.printSettings, medInstructionLang: e.target.value } }))}>
                {REGIONAL_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={settings.printSettings?.bilingualAdvice}
                onChange={(e) => setSettings((s) => ({ ...s, printSettings: { ...s.printSettings, bilingualAdvice: e.target.checked } }))} />
              Bilingual advice on Rx print
            </label>
            <label className="field" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={settings.printSettings?.showQr !== false}
                onChange={(e) => setSettings((s) => ({ ...s, printSettings: { ...s.printSettings, showQr: e.target.checked } }))} />
              Show QR on prescription
            </label>
          </div>
          <div className="card" style={{ background: '#f8fafc', marginTop: 12 }}>
            <strong>Preview</strong>
            <p>{MED_INSTRUCTION_SAMPLES.en}</p>
            <p style={{ color: '#0d6e6a' }}>{MED_INSTRUCTION_SAMPLES[settings.printSettings?.medInstructionLang] || MED_INSTRUCTION_SAMPLES.hi}</p>
          </div>
        </div>
      )}

      {tab === 'services' && (
        <div className="card">
          <h3>Services catalog — bulk upload</h3>
          <p className="hint">One service per line: Name, Fee, Category</p>
          <textarea className="input" rows={5} value={svcBulk} onChange={(e) => setSvcBulk(e.target.value)}
            placeholder={'OPD Consultation, 500, Consultation\nECG, 200, Procedure'} />
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={importServicesBulk}>Import lines</button>
          <table className="data-table" style={{ marginTop: 16 }}>
            <thead><tr><th>Service</th><th>Fee</th><th>Category</th></tr></thead>
            <tbody>
              {(settings.services || []).map((s) => (
                <tr key={s.name}><td>{s.name}</td><td>₹{s.fee}</td><td>{s.category}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'meds' && (
        <div className="card">
          <h3>Medications section</h3>
          <div className="form-grid">
            {['showGeneric', 'showDuration', 'showInstructions'].map((k) => (
              <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={settings.medSectionConfig?.[k] !== false}
                  onChange={(e) => setSettings((s) => ({ ...s, medSectionConfig: { ...s.medSectionConfig, [k]: e.target.checked } }))} />
                {k.replace('show', 'Show ')}
              </label>
            ))}
            <div className="field">
              <label>Default duration</label>
              <input className="input" value={settings.medSectionConfig?.defaultDuration || '5 days'}
                onChange={(e) => setSettings((s) => ({ ...s, medSectionConfig: { ...s.medSectionConfig, defaultDuration: e.target.value } }))} />
            </div>
          </div>
        </div>
      )}

      {tab === 'packages' && (
        <div className="card">
          <h3>Treatment packages</h3>
          <p className="hint">Packages appear in Billing → Package Billing after save.</p>
          <table className="data-table">
            <thead><tr><th>Package</th><th>Dept</th><th>Amount</th><th>Includes</th><th></th></tr></thead>
            <tbody>
              {(settings.treatmentPackages || []).map((p, i) => (
                <tr key={p.id || i}>
                  <td>{p.name}</td><td>{p.dept}</td><td>₹{Number(p.amount || 0).toLocaleString('en-IN')}</td><td>{p.includes}</td>
                  <td><button type="button" className="btn btn-sm btn-secondary" onClick={() => setSettings((s) => ({
                    ...s, treatmentPackages: (s.treatmentPackages || []).filter((_, j) => j !== i),
                  }))}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="form-grid" style={{ marginTop: 12 }}>
            <div className="field"><label>Name</label><input className="input" value={pkgDraft.name} onChange={(e) => setPkgDraft((d) => ({ ...d, name: e.target.value }))} /></div>
            <div className="field"><label>Department</label><input className="input" value={pkgDraft.dept} onChange={(e) => setPkgDraft((d) => ({ ...d, dept: e.target.value }))} /></div>
            <div className="field"><label>Amount ₹</label><input className="input" type="number" value={pkgDraft.amount} onChange={(e) => setPkgDraft((d) => ({ ...d, amount: e.target.value }))} /></div>
            <div className="field"><label>Duration</label><input className="input" value={pkgDraft.duration} onChange={(e) => setPkgDraft((d) => ({ ...d, duration: e.target.value }))} placeholder="3 nights" /></div>
            <div className="field full"><label>Includes</label><input className="input" value={pkgDraft.includes} onChange={(e) => setPkgDraft((d) => ({ ...d, includes: e.target.value }))} /></div>
          </div>
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => {
            if (!pkgDraft.name.trim()) return;
            const id = `PKG-${Date.now().toString(36).toUpperCase()}`;
            setSettings((s) => ({
              ...s,
              treatmentPackages: [...(s.treatmentPackages || []), {
                id, name: pkgDraft.name.trim(), dept: pkgDraft.dept || 'General',
                amount: Number(pkgDraft.amount) || 0, includes: pkgDraft.includes, duration: pkgDraft.duration || '—',
              }],
            }));
            setPkgDraft({ name: '', dept: 'General', amount: '', includes: '', duration: '' });
          }}>+ Add package</button>
        </div>
      )}

      {tab === 'availability' && (
        <div className="card">
          <h3>Doctor availability</h3>
          {doctors.map((d) => {
            const row = (settings.doctorAvailability || []).find((x) => x.name === d.name) || {
              name: d.name, morning: '09:00-13:00', evening: '16:00-20:00', slotMin: 20, days: 'Mon-Sat',
            };
            return (
              <div key={d.staffId} className="card mb" style={{ background: '#f8fafc' }}>
                <strong>{d.name}</strong>
                <div className="form-grid" style={{ marginTop: 8 }}>
                  <div className="field"><label>Morning</label><input className="input" defaultValue={row.morning} onBlur={(e) => {
                    setSettings((s) => {
                      const arr = [...(s.doctorAvailability || [])].filter((x) => x.name !== d.name);
                      arr.push({ ...row, morning: e.target.value });
                      return { ...s, doctorAvailability: arr };
                    });
                  }} /></div>
                  <div className="field"><label>Evening</label><input className="input" defaultValue={row.evening} onBlur={(e) => {
                    setSettings((s) => {
                      const arr = [...(s.doctorAvailability || [])].filter((x) => x.name !== d.name);
                      arr.push({ ...row, evening: e.target.value });
                      return { ...s, doctorAvailability: arr };
                    });
                  }} /></div>
                  <div className="field"><label>Slot (min)</label><input className="input" type="number" defaultValue={row.slotMin || 20} onBlur={(e) => {
                    setSettings((s) => {
                      const arr = [...(s.doctorAvailability || [])].filter((x) => x.name !== d.name);
                      arr.push({ ...row, slotMin: Number(e.target.value) });
                      return { ...s, doctorAvailability: arr };
                    });
                  }} /></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'uhid' && (
        <div className="card">
          <h3>UHID configuration</h3>
          <p className="hint">Prefix + next sequence — auto-assigned on every new patient registration.</p>
          <div className="form-grid">
            <div className="field"><label>Prefix</label><input className="input" value={uhidForm.prefix} onChange={(e) => setUhidForm({ ...uhidForm, prefix: e.target.value })} placeholder="UHID" /></div>
            <div className="field"><label>Starts from</label><input className="input" type="number" value={uhidForm.nextSeq} onChange={(e) => setUhidForm({ ...uhidForm, nextSeq: e.target.value })} /></div>
          </div>
          <p className="hint">Next UHID preview: <strong>{uhidForm.prefix}-{uhidForm.nextSeq}</strong></p>
        </div>
      )}

      {tab === 'sms' && (
        <div className="card">
          <h3>Message settings & credits</h3>
          <div className="info-banner" style={{ marginBottom: 12 }}>
            Auto SMS toggles are saved here. Messages send only when MSG91 or Twilio gateway is configured under Hospital Settings → SMS.
          </div>
          <div className="form-grid">
            <div className="field"><label>SMS credits balance</label><input className="input" type="number" value={smsCredits} onChange={(e) => setSmsCredits(e.target.value)} /></div>
            <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={settings.smsSettings?.autoApptReminder !== false}
                onChange={(e) => setSettings((s) => ({ ...s, smsSettings: { ...s.smsSettings, autoApptReminder: e.target.checked } }))} />
              Auto appointment reminder SMS
            </label>
            <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={!!settings.smsSettings?.autoReceiptSms}
                onChange={(e) => setSettings((s) => ({ ...s, smsSettings: { ...s.smsSettings, autoReceiptSms: e.target.checked } }))} />
              Auto receipt SMS after payment
            </label>
            <div className="field full"><label>SMS signature</label><input className="input" value={settings.smsSettings?.signature || ''}
              onChange={(e) => setSettings((s) => ({ ...s, smsSettings: { ...s.smsSettings, signature: e.target.value } }))} /></div>
          </div>
        </div>
      )}

      {tab === 'genie' && (
        <div className="card">
          <h3>Favourite medicines — quick add in Rx pad</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <input className="input" list="genie-drugs" value={genieInput} onChange={(e) => setGenieInput(e.target.value)}
              placeholder="Add medicine…" style={{ flex: 1, minWidth: 200 }} />
            <datalist id="genie-drugs">{DRUG_SUGGESTIONS.map((d) => <option key={d} value={d} />)}</datalist>
            <button type="button" className="btn btn-secondary" onClick={() => addGenie()}>Add</button>
          </div>
          <div className="q-tag-row">
            {genieMeds.map((m) => (
              <button key={m} type="button" className="q-tag on" onClick={() => setGenieMeds((g) => g.filter((x) => x !== m))}>{m} ×</button>
            ))}
          </div>
        </div>
      )}

      {tab === 'queue' && (
        <div className="card">
          <h3>Custom Q Page sections</h3>
          <p className="hint" style={{ fontSize: 12 }}>Add tabs on reception Q Page filtered by tag, status, or visit type.</p>
          {(settings.queueCustomSections || []).map((s, i) => (
            <div key={s.id || i} className="form-grid" style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
              <div className="field"><label>Tab label</label><input className="input" value={s.label} onChange={(e) => {
                const list = [...(settings.queueCustomSections || [])];
                list[i] = { ...list[i], label: e.target.value };
                setSettings((st) => ({ ...st, queueCustomSections: list }));
              }} /></div>
              <div className="field"><label>Filter tag</label><input className="input" value={s.tag || ''} placeholder="e.g. VIP" onChange={(e) => {
                const list = [...(settings.queueCustomSections || [])];
                list[i] = { ...list[i], tag: e.target.value, id: list[i].id || `custom-${i}` };
                setSettings((st) => ({ ...st, queueCustomSections: list }));
              }} /></div>
              <div className="field"><button type="button" className="btn btn-sm btn-secondary" onClick={() => {
                setSettings((st) => ({ ...st, queueCustomSections: (st.queueCustomSections || []).filter((_, j) => j !== i) }));
              }}>Remove</button></div>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => {
            const id = `custom-${Date.now()}`;
            setSettings((st) => ({ ...st, queueCustomSections: [...(st.queueCustomSections || []), { id, label: 'New section', tag: '' }] }));
          }}>+ Add section</button>
        </div>
      )}

      {tab === 'dental' && (
        <div className="card">
          <h3>Dental chart in EMR</h3>
          {isManager ? (
            <>
              <p className="hint">
                When enabled, the FDI odontogram appears on EMR for all doctors during consults.
                Individual doctors can also enable Dental OP on their login below or in Staff Admin.
              </p>
              <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={!!settings.enableDentalChart}
                  onChange={(e) => setSettings((s) => ({ ...s, enableDentalChart: e.target.checked }))} />
                Show FDI dental chart for all doctors in EMR
              </label>
            </>
          ) : (
            <>
              <p className="hint">
                Enable Dental OP on your login to see the FDI chart, procedure list, and dental chair board.
                Your hospital manager can also turn this on hospital-wide.
              </p>
              <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={dentalOpPersonal}
                  onChange={(e) => setDentalOpPersonal(e.target.checked)} />
                Enable Dental OP workspace on my login
              </label>
              {settings.enableDentalChart && (
                <p className="hint" style={{ marginTop: 10 }}>Hospital-wide dental chart is already enabled by your manager.</p>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'modules' && (
        <div className="card">
          <h3>Clinical modules</h3>
          <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={settings.enableVaccinationChart !== false}
              onChange={(e) => setSettings((s) => ({ ...s, enableVaccinationChart: e.target.checked }))} />
            Vaccination chart on patient profile
          </label>
          <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={settings.enableHealthAssessment !== false}
              onChange={(e) => setSettings((s) => ({ ...s, enableHealthAssessment: e.target.checked }))} />
            Health assessment in EMR
          </label>
          <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={!!settings.enableOphthalmology}
              onChange={(e) => setSettings((s) => ({ ...s, enableOphthalmology: e.target.checked }))} />
            Ophthalmology module (VA, IOP, refraction)
          </label>
        </div>
      )}

      {tab === 'doctemplates' && (
        <div className="card">
          <h3>Document templates outside Rx</h3>
          <p className="hint" style={{ fontSize: 12 }}>Use placeholders: {'{{patient}}'}, {'{{uhid}}'}, {'{{diagnosis}}'}</p>
          {(settings.docTemplates || []).map((t, i) => (
            <div key={t.id || i} className="form-grid" style={{ marginBottom: 12, borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
              <div className="field"><label>Name</label><input className="input" value={t.name} onChange={(e) => {
                const list = [...settings.docTemplates]; list[i] = { ...list[i], name: e.target.value };
                setSettings((s) => ({ ...s, docTemplates: list }));
              }} /></div>
              <div className="field full"><label>Body</label><textarea className="input" rows={4} value={t.body} onChange={(e) => {
                const list = [...settings.docTemplates]; list[i] = { ...list[i], body: e.target.value };
                setSettings((s) => ({ ...s, docTemplates: list }));
              }} /></div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setSettings((s) => ({
            ...s, docTemplates: [...(s.docTemplates || []), { id: `DOC-${Date.now()}`, name: 'New template', body: '' }],
          }))}>+ Add template</button>
        </div>
      )}

      {tab === 'rxtemplate' && (
        <div className="card">
          <h3>Customise My Rx template</h3>
          <p className="hint">Personal print options for your prescriptions. Hospital-wide print defaults are set by your manager.</p>
          <div className="form-grid">
            {[
              ['showLogo', 'Show hospital logo'], ['showDoctorReg', 'Show doctor reg. no.'],
              ['showSignature', 'Show doctor signature'], ['showQr', 'Show QR on Rx'],
              ['bilingualAdvice', 'Bilingual advice footer'],
            ].map(([key, label]) => (
              <label key={key} className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={rxPrintTemplate?.[key] !== false}
                  onChange={(e) => setRxPrintTemplate((t) => ({ ...t, [key]: e.target.checked }))} />
                {label}
              </label>
            ))}
            <div className="field full">
              <label>Rx footer text</label>
              <input className="input" value={rxPrintTemplate?.footerText || ''}
                onChange={(e) => setRxPrintTemplate((t) => ({ ...t, footerText: e.target.value }))} />
            </div>
          </div>
        </div>
      )}

      {tab === 'signature' && (
        <div className="card">
          <h3>Upload signature</h3>
          <p className="hint">PNG/JPG signature appears on printed prescriptions for {user?.name || 'you'}.</p>
          <div className="form-grid">
            <div className="field"><label>Medical council reg. no.</label>
              <input className="input" value={regNo} onChange={(e) => setRegNo(e.target.value)} placeholder="e.g. APMC/12345" />
            </div>
            <div className="field full">
              <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
                Upload signature image
                <input type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async () => {
                    const b64 = String(reader.result).split(',')[1];
                    setSignaturePreview(reader.result);
                    await ipApi.patchStaffProfile({ signatureBase64: b64, regNo });
                    setMsg('Signature saved.');
                  };
                  reader.readAsDataURL(file);
                }} />
              </label>
            </div>
            {signaturePreview && <img src={signaturePreview} alt="Signature" style={{ maxHeight: 80, border: '1px solid #e2e8f0' }} />}
          </div>
        </div>
      )}

      {tab === 'workspaces' && (
        <div className="card">
          <h3>Workspaces / branches</h3>
          <p className="hint" style={{ fontSize: 12 }}>Doctors with multiple clinics can switch workspace from the sidebar.</p>
          {(settings.workspaces || []).map((w, i) => (
            <div key={w.id || i} className="form-grid" style={{ marginBottom: 8 }}>
              <div className="field"><label>Name</label><input className="input" value={w.name} onChange={(e) => {
                const list = [...(settings.workspaces || [])]; list[i] = { ...list[i], name: e.target.value };
                setSettings((s) => ({ ...s, workspaces: list }));
              }} /></div>
              <div className="field"><button type="button" className="btn btn-sm btn-secondary" onClick={() => {
                setSettings((s) => ({ ...s, workspaces: (s.workspaces || []).filter((_, j) => j !== i) }));
              }}>Remove</button></div>
            </div>
          ))}
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => setSettings((s) => ({
            ...s, workspaces: [...(s.workspaces || []), { id: `ws-${Date.now()}`, name: 'Branch' }],
          }))}>+ Add workspace</button>
        </div>
      )}
    </PageShell>
  );
}
